import { useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

// Gestione degli auditor interni (dipendenti che vanno in azienda e compilano la checklist). Li crea il consulente:
// la password la sceglie e la consegna lui di persona, l'app non invia email.
export default function AuditorsAdmin() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [newPw, setNewPw] = useState({});

  const load = useCallback(() => apiRequest("/safety/auditors").then((r) => setRows(r.auditors)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const create = () => act(async () => {
    await apiRequest("/safety/auditors", { method: "POST", body: JSON.stringify(form) });
    setForm({ name: "", email: "", password: "" });
  }, "Auditor creato: consegnagli di persona email e password");

  return (
    <div className="space-y-4" data-testid="auditors-admin">
      <div>
        <h1 className="font-heading text-2xl font-bold">Auditor</h1>
        <p className="text-sm text-slate-600">Dipendenti che vanno in azienda, aprono da soli azienda e sopralluogo dal tablet, compilano la checklist e la inviano alla piattaforma. Le aziende che aprono arrivano a te. Non vedono rischi, rilievi né bozze: il documento lo controlli tu.</p>
      </div>
      <Card><CardHeader><CardTitle className="text-base">Nuovo auditor</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <Input placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input type="email" placeholder="Email di accesso" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input type="text" placeholder="Password (almeno 10 caratteri)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Button disabled={busy || form.name.trim().length < 2 || !form.email || form.password.length < 10} onClick={create}><UserPlus className="h-4 w-4" /> Crea</Button>
        </CardContent></Card>
      <Card><CardHeader><CardTitle className="text-base">Elenco</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-slate-500">Nessun auditor.</p>}
          {rows.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
              <div><p className="font-medium">{u.name} {!u.is_active && <Badge variant="outline">disattivato</Badge>}</p>
                <p className="text-xs text-slate-500">{u.email} · {u.assigned_surveys} sopralluoghi assegnati</p></div>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="h-9 w-52" type="text" placeholder="Nuova password" value={newPw[u.id] || ""} onChange={(e) => setNewPw({ ...newPw, [u.id]: e.target.value })} />
                <Button size="sm" variant="outline" disabled={busy || (newPw[u.id] || "").length < 10}
                  onClick={() => act(async () => { await apiRequest(`/safety/auditors/${u.id}/password`, { method: "POST", body: JSON.stringify({ password: newPw[u.id] }) }); setNewPw({ ...newPw, [u.id]: "" }); }, "Password cambiata")}>Cambia password</Button>
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => act(() => apiRequest(`/safety/auditors/${u.id}/active`, { method: "POST", body: JSON.stringify({ active: !u.is_active }) }), u.is_active ? "Disattivato" : "Riattivato")}>{u.is_active ? "Disattiva" : "Riattiva"}</Button>
              </div>
            </div>))}
        </CardContent></Card>
    </div>
  );
}
